import { useEffect, useRef, type FC } from 'react';
import { ScrollView, StyleSheet, View, StatusBar as ReactNativeStatusBar } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import Benefits from 'src/presentation/components/organisms/Benefits';
import BenefitDialog from 'src/presentation/dialogs/BenefitDialog';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import LoyaltyStatusCard from 'src/presentation/components/organisms/LoyaltyStatusCard';
import { StatusBar } from 'expo-status-bar';
import Dimens from 'src/core/utils/Dimens';
import { LoyaltyStatusVariant } from 'src/domain/models/LoyaltyStatusVariant';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import { useLoyaltyStore } from 'src/domain/stores/useLoyaltyStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import type { LoyaltyNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const LoyaltyScreen: FC = () => {
	const navigation = useNavigation<StackNavigationProp<LoyaltyNavigation>>();
	const tabBarInset = useTabBarInset();
	const { loyalty, isLoading, errorMessage, init, getApiLoyalty, replayEntrance } = useLoyaltyStore();
	const { captureEvent } = useServicesStore();

	const pageScrollView = useRef<ScrollView>(null);

	useEffect(
		() =>
			navigation.getParent()?.addListener('blur', () => {
				pageScrollView.current?.scrollTo({ x: 0, y: 0, animated: false });
			}),
		[navigation],
	);

	const fetchData = () => {
		getApiLoyalty().catch((error: ApiRequestError) =>
			captureEvent({ message: 'getApiLoyalty failed', extra: { error } }),
		);
	};

	useAppStateScreenListener({
		onInit: init,
		onFocussed: () => {
			ReactNativeStatusBar.setBarStyle('dark-content');

			replayEntrance();
			fetchData();
		},
		onForegrounded: fetchData,
	});

	return (
		<>
			<StatusBar style={'dark'} />
			<View style={styles.mainWrapper}>
				<BackgroundPattern />
				{isLoading ? (
					<LoadingIndicator style={{ flex: Dimens.flex1 }} />
				) : errorMessage ? (
					<ErrorView onPress={fetchData} />
				) : (
					<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }} ref={pageScrollView}>
						<LoyaltyStatusCard status={loyalty?.status} style={styles.statusCard} variant={LoyaltyStatusVariant.Page} />

						<Benefits benefits={loyalty?.benefits} />
					</ScrollView>
				)}
				<BenefitDialog />
			</View>
		</>
	);
};

const styles = StyleSheet.create({
	mainWrapper: {
		flex: Dimens.flex1,
	},
	statusCard: {
		marginTop: Dimens.padding20,
		marginHorizontal: Dimens.padding18,
	},
});

export default LoyaltyScreen;
